import { useEffect, useMemo, useState, type CSSProperties, type JSX, type ReactNode } from 'react';
import {
  diffDocuments,
  layoutNodes,
  nodeColour,
  type CanonicalDocument,
  type CommentaryDiff,
  type DocNode,
  type NodeDiff,
  type NodeLayout,
  type WordOp,
} from '@cca/corpus-shared';
import { ApiError, api, type RevisionMeta } from '../api/client';
import { renderNoteBody } from './noteFormat';

/**
 * Compare view (ADR-006 amendment) — the full visual diff between two document
 * versions. Read-only: the comparison itself is the pure `diffDocuments` in
 * `@cca/corpus-shared`; this component only renders the result in the liturgy's own
 * reading shape (reusing `layoutNodes` for indent + rubric numbers). Commission-only
 * (the worker gates the snapshot route). Rendered in place of the reading surface.
 */

/** A side of the comparison: a revision id, or the live document. */
export type DiffSide = string | 'current';
export interface DiffSelection {
  a: DiffSide; // base ("before")
  b: DiffSide; // compared ("after")
}

interface DiffViewProps {
  docId: string;
  current: CanonicalDocument;
  selection: DiffSelection;
  onClose: () => void;
  onReload: () => void | Promise<void>;
}

const fmt = (iso: string): string =>
  new Date(iso).toLocaleString('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const sideLabel = (meta: RevisionMeta): string =>
  meta.label ? `“${meta.label}” · ${fmt(meta.createdAt)}` : fmt(meta.createdAt);

async function resolveSide(
  docId: string,
  side: DiffSide,
  current: CanonicalDocument,
): Promise<{ snapshot: CanonicalDocument; label: string }> {
  if (side === 'current') return { snapshot: current, label: 'Current version' };
  const { meta, snapshot } = await api.getRevision(docId, side);
  return { snapshot, label: sideLabel(meta) };
}

/** Plain (un-diffed) lines of a node — for added / removed / unchanged blocks. */
function PlainLines({ node }: { node: DocNode }): JSX.Element {
  return (
    <>
      {node.lines.map((line, li) => (
        <span key={li} className={`d-line${line.response ? ' resp' : ''}`}>
          {line.runs.map((run, ri) => {
            const style: CSSProperties = {};
            if (run.bold) style.fontWeight = 700;
            if (run.italic) style.fontStyle = 'italic';
            return (
              <span key={ri} style={style}>
                {run.text}
              </span>
            );
          })}
        </span>
      ))}
    </>
  );
}

/** Word-level diffed lines — struck-through removed words, highlighted added words. */
function WordLines({ lineDiffs }: { lineDiffs: WordOp[][] }): JSX.Element {
  return (
    <>
      {lineDiffs.map((ops, li) => (
        <span key={li} className="d-line">
          {ops.map((op, oi) => {
            if (op.type === 'add')
              return (
                <ins key={oi} className="d-add">
                  {op.text}
                </ins>
              );
            if (op.type === 'del')
              return (
                <del key={oi} className="d-del">
                  {op.text}
                </del>
              );
            return <span key={oi}>{op.text}</span>;
          })}
        </span>
      ))}
    </>
  );
}

const STATUS_CLASS: Record<NodeDiff['status'], string> = {
  added: 'd-block-add',
  removed: 'd-block-del',
  edited: 'd-block-edit',
  moved: 'd-block-moved',
  unchanged: 'd-unchanged',
};

export function DiffView({
  docId,
  current,
  selection,
  onClose,
  onReload,
}: DiffViewProps): JSX.Element {
  const [loaded, setLoaded] = useState<{
    before: CanonicalDocument;
    after: CanonicalDocument;
    beforeLabel: string;
    afterLabel: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showUnchanged, setShowUnchanged] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(null);
    setError(null);
    void (async () => {
      try {
        const [a, b] = await Promise.all([
          resolveSide(docId, selection.a, current),
          resolveSide(docId, selection.b, current),
        ]);
        if (!cancelled) {
          setLoaded({
            before: a.snapshot,
            after: b.snapshot,
            beforeLabel: a.label,
            afterLabel: b.label,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : 'Could not load the versions to compare.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [docId, selection.a, selection.b, current]);

  const diff = useMemo(
    () => (loaded ? diffDocuments(loaded.before, loaded.after) : null),
    [loaded],
  );
  const afterLayout = useMemo(() => (loaded ? layoutNodes(loaded.after.nodes) : null), [loaded]);
  const beforeLayout = useMemo(() => (loaded ? layoutNodes(loaded.before.nodes) : null), [loaded]);

  const restoreBase = async () => {
    if (selection.a === 'current') return;
    if (
      !window.confirm(
        'Restore the whole document to the A version? Your current version is saved as a "before restore" checkpoint first, so you can undo this.',
      )
    )
      return;
    setBusy(true);
    try {
      await api.restoreRevision(docId, selection.a);
      await onReload();
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not restore that version.');
      setBusy(false);
    }
  };

  const changedNotes = diff?.commentary.filter((c) => c.status !== 'unchanged') ?? [];
  // Group changed notes by the block they annotate, so they render in context.
  const notesByTarget = new Map<string, CommentaryDiff[]>();
  for (const c of changedNotes) {
    const target = (c.after ?? c.before)?.targetNodeId;
    if (!target) continue;
    notesByTarget.set(target, [...(notesByTarget.get(target) ?? []), c]);
  }
  const shownIds = new Set(diff?.nodes.map((n) => n.id) ?? []);
  // Notes whose anchor block isn't in the list (rare) — shown at the foot instead.
  const orphanNotes = changedNotes.filter((c) => {
    const t = (c.after ?? c.before)?.targetNodeId;
    return !t || !shownIds.has(t);
  });

  const renderNote = (c: CommentaryDiff): JSX.Element | null => {
    const note = c.after ?? c.before;
    if (!note) return null;
    const cls =
      c.status === 'added' ? 'd-note-add' : c.status === 'removed' ? 'd-note-del' : 'd-note-edit';
    return (
      <div key={c.id} className={`d-note ${cls}`}>
        <span className="d-note-tags">
          <span className="d-chip">{note.kind}</span>
          <span className="d-chip">{note.audience}</span>
          <span className="d-note-status">{c.status}</span>
          {c.metaChanged?.map((m) => (
            <span key={m} className="d-chip d-chip-meta">
              {m} changed
            </span>
          ))}
        </span>
        <div className="d-note-body">{renderNoteBody(note.body)}</div>
      </div>
    );
  };

  const renderEntry = (entry: NodeDiff): JSX.Element | null => {
    const node = entry.status === 'removed' ? entry.before : entry.after;
    if (!node) return null; // 'removed' always has `before`, others `after` — guard for the type.
    const notes = notesByTarget.get(node.id) ?? [];
    const layout: NodeLayout | null = entry.status === 'removed' ? beforeLayout : afterLayout;
    const level = layout?.indentLevelById.get(node.id) ?? 0;
    const rubricNo = layout?.rubricNumberById.get(node.id);
    const colour = nodeColour(node);
    return (
      <div
        key={entry.id}
        className={`d-entry ${STATUS_CLASS[entry.status]} type-${node.type} colour-${colour}`}
        style={{ marginLeft: `${level * 1.4}rem` }}
      >
        {entry.status === 'moved' && (
          <span className="d-move-badge">
            Moved here{entry.move?.reParented ? ' (re-nested)' : ' (re-ordered)'}
          </span>
        )}
        <div className="d-entry-body">
          {rubricNo !== undefined && <span className="d-rubric-n">{rubricNo}.</span>}
          {node.label && <span className="d-label">{node.label}</span>}
          {entry.lineDiffs ? <WordLines lineDiffs={entry.lineDiffs} /> : <PlainLines node={node} />}
        </div>
        {notes.length > 0 && <div className="d-entry-notes">{notes.map(renderNote)}</div>}
      </div>
    );
  };

  // Render the block list with a few blocks of UNCHANGED context around each change
  // or annotated block (git-style), so every edit/note reads in its surrounding
  // text. "Show unchanged" reveals the whole document; the gaps collapse to a
  // "⋯ N unchanged" marker.
  const CONTEXT = 2;
  const renderDiffList = (entries: NodeDiff[]): JSX.Element[] => {
    const anchor = new Set<number>();
    entries.forEach((e, i) => {
      const node = e.status === 'removed' ? e.before : e.after;
      const hasNote = node ? (notesByTarget.get(node.id)?.length ?? 0) > 0 : false;
      if (e.status !== 'unchanged' || hasNote) anchor.add(i);
    });
    const visible = (i: number): boolean => {
      if (showUnchanged) return true;
      for (const a of anchor) if (Math.abs(a - i) <= CONTEXT) return true;
      return false;
    };
    const out: JSX.Element[] = [];
    let hidden = 0;
    const flushGap = (key: string) => {
      if (hidden > 0) {
        out.push(
          <div key={key} className="d-gap">
            ⋯ {hidden} unchanged block{hidden === 1 ? '' : 's'}
          </div>,
        );
        hidden = 0;
      }
    };
    entries.forEach((e, i) => {
      if (visible(i)) {
        flushGap(`gap-${i}`);
        const el = renderEntry(e);
        if (el) out.push(el);
      } else {
        hidden++;
      }
    });
    flushGap('gap-end');
    return out;
  };

  const summary = (): ReactNode => {
    if (!diff) return null;
    const c = diff.counts;
    const parts: string[] = [];
    const add = (n: number, w: string) => n && parts.push(`${n} ${w}${n === 1 ? '' : 's'}`);
    add(c.nodesAdded, 'block added');
    add(c.nodesRemoved, 'block removed');
    add(c.nodesEdited, 'block edited');
    add(c.nodesMoved, 'block moved');
    add(c.notesAdded, 'note added');
    add(c.notesRemoved, 'note removed');
    add(c.notesEdited, 'note edited');
    return parts.length ? parts.join(' · ') : 'No differences.';
  };

  return (
    <section className="diff-panel" aria-label="Compare versions">
      <div className="diff-head">
        <h2>Compare versions</h2>
        {loaded && (
          <p className="diff-versions">
            <span className="diff-side-a">{loaded.beforeLabel}</span>
            <span className="diff-arrow"> → </span>
            <span className="diff-side-b">{loaded.afterLabel}</span>
          </p>
        )}
        <div className="diff-actions">
          {diff && (
            <button type="button" className="link-btn" onClick={() => setShowUnchanged((v) => !v)}>
              {showUnchanged ? 'Hide unchanged' : 'Show unchanged'}
            </button>
          )}
          {selection.a !== 'current' && (
            <button type="button" className="link-btn" disabled={busy} onClick={restoreBase}>
              Restore the A version
            </button>
          )}
          <button type="button" className="link-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {error && <p className="diff-error">{error}</p>}

      {!loaded && !error && <p className="diff-empty">Loading…</p>}

      {diff && (
        <>
          <p className="diff-summary">{summary()}</p>
          {(diff.documentMeta.titleChanged ||
            diff.documentMeta.subtitleChanged ||
            diff.documentMeta.notesChanged) && (
            <ul className="diff-doc-meta">
              {diff.documentMeta.titleChanged && (
                <li>
                  Title: <span className="d-del">{diff.documentMeta.prevTitle}</span> →{' '}
                  <span className="d-add">{loaded?.after.document.title}</span>
                </li>
              )}
              {diff.documentMeta.subtitleChanged && <li>Subtitle changed</li>}
              {diff.documentMeta.notesChanged && <li>Working notes changed</li>}
            </ul>
          )}

          <div className="diff-list">{renderDiffList(diff.nodes)}</div>

          {orphanNotes.length > 0 && (
            <div className="diff-notes">
              <h3>Other changed notes</h3>
              {orphanNotes.map(renderNote)}
            </div>
          )}
        </>
      )}
    </section>
  );
}
